// src/admin/pages/AdminAddCustomer.js
import React, { useState, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import { FaChevronRight, FaInbox } from "react-icons/fa";
import { FiAlertCircle } from "react-icons/fi";
import { FaUserPlus, FaEye, FaEyeSlash } from "react-icons/fa";
import { adminApiRequest } from "../../services/adminApi";
import { createAdminCustomer } from "../../services/adminCustomers.api";

function AdminAddCustomer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useContext(AdminLanguageContext);
  
  const [errorToast, setErrorToast] = useState("");
  
  const nameRef = React.useRef(null);
  const phoneRef = React.useRef(null);
  const passwordRef = React.useRef(null);

  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  const [debounceTimer, setDebounceTimer] = useState(null);

  const [showToast, setShowToast] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
const [showCustomerPassword, setShowCustomerPassword] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [errors, setErrors] = useState({});

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const [customer, setCustomer] = useState({
  name: "",
  phone: "",
  email: "",
  password: "",
  country: "Lebanon",
  governorate: "",
  area: "",
  street: "",
  building: "",
  note: "",
  businessName: "",
  instagramName: "",
  instagramLink: "",
  wholesaleRequestId: "",
});

useEffect(() => {
  const incoming = location.state?.customer;

  if (!location.state?.fromWholesaleRequest || !incoming) return;

  setCustomer((prev) => ({
    ...prev,
    name: incoming.name || "",
    phone: incoming.phone || "",
    email: incoming.email || "",
    businessName: incoming.businessName || "",
    instagramName: incoming.instagramName || "",
    instagramLink: incoming.instagramLink || "",
    wholesaleRequestId: location.state?.wholesaleRequestId || "",
  }));

  setIsDirty(true);
}, [location.state]);

  const isFormValid =
  customer.name.trim() &&
  customer.phone.trim() &&
  customer.password.trim();

  const LEBANON_GOVERNORATES = {
  EN: ["Beirut","Mount Lebanon","North","South","Bekaa","Nabatieh","Akkar","Baalbek-Hermel"],
  AR: ["بيروت","جبل لبنان","الشمال","الجنوب","البقاع","النبطية","عكار","بعلبك الهرمل"]
}[lang];

  const TEXT = {
    EN: {
      title: "Create customer",
      back: "Customers",
      name: "Username",
password: "Password",
      phone: "Phone",
      email: "Email",
      governorate: "Governorate",
      area: "Area",
      select: "Select",
      street: "Street",
      building: "Building",
      businessName: "Business name",
instagramName: "Instagram username",
instagramLink: "Instagram account link",
wholesaleInfo: "Wholesale information",
      note: "Note",
country: "Country",
      cancel: "Cancel",
      save: "Save",
      create: "Create customer",
    },
    AR: {
      title: "إنشاء عميل",
      back: "العملاء",
      name: "اسم المستخدم",
password: "كلمة المرور",
      phone: "الهاتف",
      businessName: "اسم العمل",
instagramName: "اسم مستخدم الإنستغرام",
instagramLink: "رابط حساب الإنستغرام",
country: "الدولة",
wholesaleInfo: "معلومات الجملة",
      email: "البريد الإلكتروني",
      governorate: "المحافظة",
      area: "المنطقة",
      select: "اختر",
      street: "الشارع",
      building: "المبنى",
      note: "ملاحظة",
      cancel: "إلغاء",
      save: "حفظ",
      create: "إنشاء عميل",
    },
  }[lang];

  useEffect(() => {
  const beforeUnload = (e) => {
    if (!isDirty) return;
    e.preventDefault();
    e.returnValue = "";
  };

  window.addEventListener("beforeunload", beforeUnload);
  return () => window.removeEventListener("beforeunload", beforeUnload);
}, [isDirty]);

useEffect(() => {
  const handleKey = (e) => {
    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
      if (isFormValid) submit();
    }
  };

  window.addEventListener("keydown", handleKey);
  return () => window.removeEventListener("keydown", handleKey);
}, [customer]);

  useEffect(() => {
    if (
  customer.name ||
  customer.phone ||
  customer.email ||
  customer.password ||
  customer.area
) {
  setIsDirty(true);
}
  }, [customer]);

  const debounceCheckDuplicate = (field, value) => {
  if (debounceTimer) clearTimeout(debounceTimer);

  const timer = setTimeout(async () => {
    await checkDuplicate();
  }, 500); // 500ms delay

  setDebounceTimer(timer);
};

  const checkDuplicate = async () => {
  try {
    setCheckingDuplicate(true);

    const res = await adminApiRequest("/admin/customers/check-duplicate", {
      method: "POST",
      body: JSON.stringify({
        name: customer.name,
        phone: customer.phone,
        email: customer.email || null,
      }),
    });

    if (res?.exists) {
      const newErrors = {};

      if (res.match?.name) {
        newErrors.name = lang === "AR"
          ? "الاسم موجود مسبقاً"
          : "Name already exists";
      }

      if (res.match?.phone) {
        newErrors.phone = lang === "AR"
          ? "رقم الهاتف مستخدم مسبقاً"
          : "Phone already exists";
      }

      if (res.match?.email) {
        newErrors.email = lang === "AR"
          ? "البريد الإلكتروني مستخدم"
          : "Email already exists";
      }

      setErrors(newErrors);

      return false;
    }

    return true;
  } catch (e) {
    console.error("duplicate check error", e);
    return true;
  } finally {
    setCheckingDuplicate(false);
  }
};

  const focusFirstError = (errs) => {
  if (errs.name) return nameRef.current?.focus();
  if (errs.phone) return phoneRef.current?.focus();
  if (errs.password) return passwordRef.current?.focus();
};

  const handleNavigate = (path) => {
  if (!isDirty) {
    navigate(path);
  } else {
    setPendingNavigation(() => () => navigate(path));
    setShowLeaveConfirm(true);
  }
};

const formatPhone = (value) => value;

 const submit = async () => {
  if (isSaving) return;

  const newErrors = {};

if (!customer.name.trim()) {
  newErrors.name = lang === "AR" ? "اسم المستخدم مطلوب" : "Username is required";
}
if (!customer.phone.trim()) {
  newErrors.phone = lang === "AR" ? "الرقم مطلوب" : "Phone number is required";
}
if (!customer.password.trim()) {
  newErrors.password = lang === "AR" ? "كلمة المرور مطلوبة" : "Password is required";
}

setErrors(newErrors);

if (Object.keys(newErrors).length > 0) {
  focusFirstError(newErrors);
  return;
}

setIsSaving(true);

/* ✅ ADD THIS BLOCK HERE */
const isUnique = await checkDuplicate();
if (!isUnique) {
  setIsSaving(false);

  // ✅ show toast
  setErrorToast(
    lang === "AR"
      ? "هذا العميل موجود مسبقاً"
      : "Customer already exists"
  );

  setTimeout(() => setErrorToast(""), 2500);

  // ✅ force focus using current values
  if (customer.phone) {
    phoneRef.current?.focus();
  } else {
    nameRef.current?.focus();
  }

  return;
}

try {
  
await createAdminCustomer({
  name: customer.name,
  phone: customer.phone,
  email: customer.email || null,
  password: customer.password,
  country: customer.country,
  governorate: customer.governorate,
  area: customer.area,
  street: customer.street,
  building: customer.building,
  note: customer.note,
  businessName: customer.businessName,
  instagramName: customer.instagramName,
  instagramLink: customer.instagramLink,
  wholesaleRequestId: customer.wholesaleRequestId,
});

setShowToast(true);
setIsSaving(false);
setIsDirty(false);

setTimeout(() => {
  navigate("/admin/customers");
}, 1200);
    } catch (e) {
  console.error(e);

  setIsSaving(false);

  alert(e.message || (lang === "AR" ? "فشل إنشاء العميل" : "Failed to create customer"));
}
  };

  const optionalText = lang === "AR" ? " (اختياري)" : " (optional)";

  return (
    <div className="flex bg-[#f1f1f1] min-h-screen">
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "AR" ? "lg:mr-26" : "lg:ml-64"}`}>
        <AdminHeader
  setIsOpen={setIsOpen}
  customCenter={
    <div
      className={`
        hidden sm:block
        w-full
        sm:max-w-[500px]
        max-w-full
        transition-all duration-300
        ${isDirty ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"}
      `}
    >
      <div className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-md px-3 py-2 flex items-center justify-between">

        {/* LEFT */}
        <div className="flex items-center gap-2 text-[13px] text-gray-200">
          <FiAlertCircle className="text-white text-[16px]" />
          <span>
            {lang === "AR" ? "عميل غير محفوظ" : "Unsaved customer"}
          </span>
        </div>

        {/* RIGHT */}
        <div className="flex gap-2">

          {/* DISCARD */}
          <button
            type="button"
           onClick={() => handleNavigate("/admin/customers")}
            className="px-2 py-[2px] text-[12px] rounded-md border border-[#3a3a3a] bg-[#1f1f1f] hover:bg-[#363636] text-gray-200 transition"
          >
            {lang === "AR" ? "إلغاء" : "Discard"}
          </button>

          {/* SAVE */}
          <button
            type="button"
            onClick={submit}
            disabled={!isFormValid}
            className={`
              px-2 py-[2px]
              text-[12px]
              rounded-md
              font-medium
              transition
              ${
                isFormValid
                  ? "bg-white text-black hover:bg-gray-200"
                  : "bg-gray-400 text-gray-200 cursor-not-allowed"
              }
            `}
          >
            {lang === "AR" ? "حفظ" : "Save"}
          </button>

        </div>
      </div>
    </div>
  }
/>

        <main className={`xl:p-3 sm:p-5 transition-all duration-300 ${isSaving ? "opacity-50 scale-[0.995]" : "opacity-100 scale-100"}`}>
          <div className="max-w-[1040px] mx-auto">

            {/* TOP BAR (IDENTICAL STYLE) */}
            <div className="mx-3 mb-2 mt-5 flex items-center gap-2 text-sm">

              <button
                onClick={() => handleNavigate("/admin/customers")}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-200"
              >
                <FaUserPlus className="text-[16px] text-gray-900" />
              </button>

              <FaChevronRight className="text-gray-400 text-[14px]" />

              <span className="font-semibold text-[#202223]">
                {TEXT.title}
              </span>
            </div>

            {/* GRID SAME AS ORDERS */}
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-3">

              {/* LEFT */}
              <div className="space-y-3">
 
{Object.keys(errors).length > 0 && (
  <div className="mx-5 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-[10px] text-sm">
    {lang === "AR"
      ? "هذه البيانات موجودة مسبقاً لعميل آخر"
      : "This data already exists for another customer"}
  </div>
)}

                {/* CONTACT CARD */}
                <section className="bg-white border border-[#e3e3e3] rounded-[14px] shadow-[0_8px_24px_rgba(0,0,0,0.06)] overflow-hidden">
                  <div className="px-5 py-4 border-b bg-[#f6f6f7]">
  <h2 className="text-[14px] font-semibold text-[#202223]">
    {lang === "AR" ? "معلومات العميل" : "Customer information"}
  </h2>
</div>
                  <div className="p-5 grid md:grid-cols-2 gap-4">

                    {/* NAME */}
                    <div>
                      <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
                        {TEXT.name}
                      </label>
                      <input
  ref={nameRef}
  type="text"
  name="etika-customer-username"
  autoComplete="new-password"
  value={customer.name}
  onChange={(e) =>
    setCustomer({ ...customer, name: e.target.value })
  }
  className={`
    w-full h-[40px]
    border
    rounded-[10px]
    px-3
    text-sm
    bg-white
    focus:outline-none
    focus:ring-2
    ${errors.name ? "border-red-500 ring-red-200" : "border-[#c9cccf] focus:ring-[#d9d9d9]"}
  `}
/>
                      {errors.name && (
  <p className="text-[12px] text-red-500 mt-1">
    {errors.name}
  </p>
)}
                    </div>

                    {/* PHONE */}
                    <div>
                      <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
                        {TEXT.phone}
                      </label>
                      <div className="relative">
    <input
      ref={phoneRef}
      value={customer.phone}
      onChange={(e) => {
const value = e.target.value;

setCustomer({
  ...customer,
  phone: value,
});

debounceCheckDuplicate("phone", value);
      }}
                        className={`
w-full h-[40px]
border
rounded-[10px]
px-3
text-sm
bg-white
focus:outline-none
focus:ring-2
${errors.phone ? "border-red-500 ring-red-200" : "border-[#c9cccf] focus:ring-[#d9d9d9]"}
`}
                      />
                        {checkingDuplicate && (
      <div className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
    )}
  </div>
                      {errors.phone && (
  <p className="text-[12px] text-red-500 mt-1">
    {errors.phone}
  </p>
)}
                    </div>

{/* PASSWORD */}
<div>
  <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
    {TEXT.password}
  </label>
  <div className="relative">
  <input
    ref={passwordRef}
    type={showCustomerPassword ? "text" : "password"}
    name="etika-customer-passcode"
    autoComplete="new-password"
    value={customer.password}
    onChange={(e) =>
      setCustomer({ ...customer, password: e.target.value })
    }
    className={`
      w-full h-[40px]
      border
      rounded-[10px]
      px-3
      ${lang === "AR" ? "pl-10" : "pr-10"}
      text-sm
      bg-white
      focus:outline-none
      focus:ring-2
      ${errors.password ? "border-red-500 ring-red-200" : "border-[#c9cccf] focus:ring-[#d9d9d9]"}
    `}
  /> 

  <button
    type="button"
    onClick={() => setShowCustomerPassword((v) => !v)}
    className={`
      absolute top-1/2 -translate-y-1/2
      ${lang === "AR" ? "left-3" : "right-3"}
      text-gray-500 hover:text-black
    `}
  >
    {showCustomerPassword ? <FaEyeSlash /> : <FaEye />}
  </button>
</div>
  {errors.password && (
    <p className="text-[12px] text-red-500 mt-1">
      {errors.password}
    </p>
  )}
</div>

                    {/* EMAIL */}
                    <div>
                      <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
                        {TEXT.email}{optionalText}
                      </label>
                       <div className="relative">
    <input
      value={customer.email}
      onChange={(e) => {
        const val = e.target.value;

        setCustomer({ ...customer, email: val });

        debounceCheckDuplicate("email", val);
      }}
                        className="
w-full h-[40px]
border border-[#c9cccf]
rounded-[10px]
px-3
text-sm
bg-white
focus:outline-none
focus:ring-2 focus:ring-[#d9d9d9]
"
                      />
                        {checkingDuplicate && (
      <div className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
    )}
  </div>
                      {errors.email && (
  <p className="text-[12px] text-red-500 mt-1">
    {errors.email}
  </p>
)}
                    </div>

{/* COUNTRY */}
<div>
  <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
    {TEXT.country}{optionalText}
  </label>

  <select
    value={customer.country}
    onChange={(e) =>
      setCustomer({
        ...customer,
        country: e.target.value,
        governorate: "",
      })
    }
    className="
      w-full h-[40px]
      border border-[#c9cccf]
      rounded-[10px]
      px-3
      text-sm
      bg-white
      focus:outline-none
      focus:ring-2 focus:ring-[#d9d9d9]
    "
  >
    <option value="Lebanon">
      {lang === "AR" ? "لبنان" : "Lebanon"}
    </option>
    <option value="Syria">
      {lang === "AR" ? "سوريا" : "Syria"}
    </option>
  </select>
</div>

{/* GOVERNORATE */}
<div>
  <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
    {TEXT.governorate}{optionalText}
  </label>

  {customer.country === "Lebanon" ? (
    <select
      value={customer.governorate}
      onChange={(e) =>
        setCustomer({ ...customer, governorate: e.target.value })
      }
      className="
        w-full h-[40px]
        border border-[#c9cccf]
        rounded-[10px]
        px-3
        text-sm
        bg-white
        focus:outline-none
        focus:ring-2 focus:ring-[#d9d9d9]
      "
    >
      <option value="">{TEXT.select}</option>
      {LEBANON_GOVERNORATES.map((g) => (
        <option key={g} value={g}>
          {g}
        </option>
      ))}
    </select>
  ) : (
    <input
      value={customer.governorate}
      onChange={(e) =>
        setCustomer({ ...customer, governorate: e.target.value })
      }
      className="
        w-full h-[40px]
        border border-[#c9cccf]
        rounded-[10px]
        px-3
        text-sm
        bg-white
        focus:outline-none
        focus:ring-2 focus:ring-[#d9d9d9]
      "
      placeholder={lang === "AR" ? "اكتب المحافظة" : "Enter governorate"}
    />
  )}
</div>
                    {/* AREA */}
                    <div>
                      <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
                        {TEXT.area}{optionalText}
                      </label>
                      <input
                        value={customer.area}
                        onChange={(e) =>
                          setCustomer({ ...customer, area: e.target.value })
                        }
                        className="
w-full h-[40px]
border border-[#c9cccf]
rounded-[10px]
px-3
text-sm
bg-white
focus:outline-none
focus:ring-2 focus:ring-[#d9d9d9]
"
                      />
                    </div>

                    {/* STREET */}
                    <div>
                      <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
                        {TEXT.street}{optionalText}
                      </label>
                      <input
                        value={customer.street}
                        onChange={(e) =>
                          setCustomer({ ...customer, street: e.target.value })
                        }
                        className="
w-full h-[40px]
border border-[#c9cccf]
rounded-[10px]
px-3
text-sm
bg-white
focus:outline-none
focus:ring-2 focus:ring-[#d9d9d9]
"
                      />
                    </div>

                    {/* BUILDING */}
                    <div>
                      <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
                        {TEXT.building}{optionalText}
                      </label>
                      <input
                        value={customer.building}
                        onChange={(e) =>
                          setCustomer({ ...customer, building: e.target.value })
                        }
                       className="
w-full h-[40px]
border border-[#c9cccf]
rounded-[10px]
px-3
text-sm
bg-white
focus:outline-none
focus:ring-2 focus:ring-[#d9d9d9]
"
                      />
                    </div>
{/* BUSINESS NAME */}
<div>
  <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
    {TEXT.businessName}{optionalText}
  </label>
  <input
    value={customer.businessName}
    onChange={(e) =>
      setCustomer({ ...customer, businessName: e.target.value })
    }
    className="
      w-full h-[40px]
      border border-[#c9cccf]
      rounded-[10px]
      px-3
      text-sm
      bg-white
      focus:outline-none
      focus:ring-2 focus:ring-[#d9d9d9]
    "
  />
</div>

{/* INSTAGRAM NAME */}
<div>
  <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
    {TEXT.instagramName}{optionalText}
  </label>
  <input
    value={customer.instagramName}
    onChange={(e) =>
      setCustomer({ ...customer, instagramName: e.target.value })
    }
    className="
      w-full h-[40px]
      border border-[#c9cccf]
      rounded-[10px]
      px-3
      text-sm
      bg-white
      focus:outline-none
      focus:ring-2 focus:ring-[#d9d9d9]
    "
  />
</div>

{/* INSTAGRAM LINK */}
<div>
  <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
    {TEXT.instagramLink}{optionalText}
  </label>
  <input
    value={customer.instagramLink}
    onChange={(e) =>
      setCustomer({ ...customer, instagramLink: e.target.value })
    }
    className="
      w-full h-[40px]
      border border-[#c9cccf]
      rounded-[10px]
      px-3
      text-sm
      bg-white
      focus:outline-none
      focus:ring-2 focus:ring-[#d9d9d9]
    "
  />
</div>
                    {/* NOTE */}
                    <div>
                      <label className="text-xs font-semibold text-[#6d7175] mb-2 block">
                        {TEXT.note}{optionalText}
                      </label>
                      <input
                        value={customer.note}
                        onChange={(e) =>
                          setCustomer({ ...customer, note: e.target.value })
                        }
                        className="
w-full h-[40px]
border border-[#c9cccf]
rounded-[10px]
px-3
text-sm
bg-white
focus:outline-none
focus:ring-2 focus:ring-[#d9d9d9]
"
                      />

                    </div>

                  </div>
                </section>
 {/* ================= BOTTOM SAVE BAR ================= */}
<div className="mt-4 bg-white border border-[#e3e3e3] rounded-[14px] shadow-[0_6px_18px_rgba(0,0,0,0.05)]">

  <div className="px-5 py-4 bg-[#f6f6f7] border-t border-[#e1e3e5]">
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

      <p className="text-sm text-[#4a4a4a]">
        {lang === "AR"
          ? "املأ بيانات العميل ثم احفظ"
          : "Fill customer details then save"}
      </p>

      <div className="flex gap-3">

        <button
          onClick={() => handleNavigate("/admin/customers")}
          className="h-[36px] text-sm px-5 rounded-[10px] border border-[#c9cccf] bg-white text-[#202223] font-medium hover:bg-[#f1f2f3]"
        >
          {TEXT.cancel}
        </button>

        <button
          onClick={submit}
          disabled={!isFormValid}
          className={`
            h-[36px] text-sm px-5 rounded-[10px] font-medium transition
            ${
              isFormValid
                ? "bg-[#202223] text-white hover:bg-black"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }
          `}
        >
          {TEXT.create}
        </button>

      </div>

    </div>
  </div>

</div>
              </div>

            

            </div>
          </div>
        </main>
      </div>
      {showLeaveConfirm && (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-[1px] z-50 flex items-center justify-center p-4">
    <div className="w-full max-w-[420px] bg-white rounded-[18px] border border-[#e1e3e5] shadow-xl overflow-hidden">

      {/* HEADER */}
      <div className="px-5 py-4 border-b border-[#e1e3e5]">
        <h3 className="text-lg font-semibold text-[#202223]">
          {lang === "AR" ? "مغادرة الصفحة؟" : "Leave page?"}
        </h3>
      </div>

      {/* BODY */}
      <div className="p-5 text-sm text-[#202223]">
        {lang === "AR"
          ? "لديك تغييرات غير محفوظة. إذا غادرت، سيتم فقدانها."
          : "You have unsaved changes. If you leave, they will be lost."}
      </div>

      {/* FOOTER */}
      <div className="px-5 py-4 border-t border-[#e1e3e5] flex justify-end gap-3">

        <button
          onClick={() => setShowLeaveConfirm(false)}
          className="h-[36px] px-4 border border-[#c9cccf] rounded-[10px]"
        >
          {TEXT.cancel}
        </button>

        <button
          onClick={() => {
            setShowLeaveConfirm(false);
            pendingNavigation && pendingNavigation();
          }}
          className="h-[36px] px-4 bg-[#202223] text-white rounded-[10px]"
        >
          {lang === "AR" ? "مغادرة" : "Leave"}
        </button>

      </div>

    </div>
  </div>
)}

{showToast && (
  <div className="fixed bottom-5 right-5 z-[9999] animate-fadeSlide">
    <div className="flex items-center gap-3 px-4 py-3 bg-[#202223] text-white rounded-lg shadow text-sm font-medium min-w-[220px]">
      
      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 text-xs">
        ✓
      </div>

      <span>
        {lang === "AR" ? "تم إنشاء العميل بنجاح" : "Customer created"}
      </span>

    </div>
  </div>
)}

{errorToast && (
  <div className="fixed bottom-5 right-5 z-[9999] animate-fadeSlide">
    <div className="flex items-center gap-3 px-4 py-3 bg-red-600 text-white rounded-lg shadow text-sm font-medium min-w-[220px]">
      
      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 text-xs">
        !
      </div>

      <span>{errorToast}</span>

    </div>
  </div>
)}
{isSaving && (
  <div className="fixed inset-0 bg-white/70 backdrop-blur-[2px] z-40 flex flex-col items-center justify-center gap-3">

    <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>

    <span className="text-sm text-gray-900 font-medium">
      {lang === "AR" ? "جاري إنشاء العميل..." : "Creating customer..."}
    </span>

  </div>
)}
    </div>
  );
}

export default AdminAddCustomer;
